'use client';

import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FieldPath, FieldValues, useController } from 'react-hook-form';
import { useDebounceValue } from 'usehooks-ts';
import { AddressAutocompleteField } from '@/features/address/ui/address-autocomplete-field';
import { QueryApi } from '@/features/query/api';
import { formatAddress } from '@/shared/lib/formatAddress';
import { toLocationBias } from '@/shared/lib/geo';
import { useGeolocation } from '@/shared/lib/hooks/useGeolocation';
import { AddressInput } from '@/shared/model/Address';
import type { Country } from '@/shared/model/Country';
import { FormFieldMessage } from './form-field-message';
import { FormFieldProps } from './types';
import { getFormFieldState } from './utils/getFormFieldState';
import { mergeRefs } from './utils/mergeRefs';
import { parseRulesForInputAttributes } from './utils/parseRulesForInputAttributes';

type Props<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<
    FormFieldProps<typeof AddressAutocompleteField, TFieldValues, TName>,
    'getCurrentPosition' | 'textValue'
>;

export const FormAddressAutocompleteField = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    name,
    control,
    defaultValue,
    disabled,
    rules,
    shouldUnregister,
    messageWrapperProps,
    isErrorVisible = true,
    isSuccessVisible,
    ref,
    onTextValueChange,
    ...props
}: Props<TFieldValues, TName>) => {
    const {
        field: { ref: fieldRef, onChange, value, ...field },
        fieldState: { error, isDirty, isTouched, invalid },
    } = useController({ ...{ name, control, defaultValue, disabled, rules, shouldUnregister } });

    const { getCurrentPosition, latitude, longitude } = useGeolocation();
    const [textValue, setTextValue] = useState(value ? formatAddress(value) : '');
    const [debouncedTextValue] = useDebounceValue(textValue, 500);
    const [isCurrentPositionUsed, setIsCurrentPositionUsed] = useState(false);

    const venueQuery = useQuery(QueryApi.Venue.queries.Venue);
    const venueCountry = venueQuery.data?.address?.country as Country | undefined;
    // Bias suggestions toward the current venue's GPS so nearby addresses rank first.
    const venueLocationBias = venueQuery.data?.address ? toLocationBias(venueQuery.data.address) : undefined;

    const { data: suggestions, isLoading: isGeocodingLoading } = useQuery({
        ...QueryApi.Geolocation.queries.Geocode({
            query: debouncedTextValue,
            countries: venueCountry ? [venueCountry] : undefined,
            locationBias: venueLocationBias,
        }),
        enabled: debouncedTextValue.length > 3 && !isCurrentPositionUsed && !!venueCountry,
    });

    const { data: reverseSuggestions, isLoading: isReverseGeocodingLoading } = useQuery({
        ...QueryApi.Geolocation.queries.ReverseGeocode({
            lat: latitude ?? 0,
            lon: longitude ?? 0,
        }),
        enabled: isCurrentPositionUsed && latitude != null && longitude != null,
    });

    useEffect(() => {
        if (isCurrentPositionUsed && reverseSuggestions && reverseSuggestions.length > 0) {
            const firstSuggestion = reverseSuggestions[0];
            const address: AddressInput = {
                city: firstSuggestion.city,
                country: firstSuggestion.country,
                postCode: firstSuggestion.postCode,
                street: firstSuggestion.street,
                lat: String(firstSuggestion.lat),
                lon: String(firstSuggestion.lon),
            };
            onChange(address);
            props.onChange?.(address);
            setTextValue(formatAddress(address));
        }
    }, [reverseSuggestions, isCurrentPositionUsed, onChange]);

    // Sync displayed text when the form value is set externally (e.g., from another component).
    // Skip when value is `{}` — that's the mid-typing state where textValue is the source of truth.
    useEffect(() => {
        const formatted = formatAddress(value);
        if (formatted || !value) {
            setTextValue(formatted);
        }
    }, [value]);

    const state = getFormFieldState({
        invalid,
        isDirty,
        isTouched,
        isErrorVisible,
        isSuccessVisible,
    });
    return (
        <>
            <AddressAutocompleteField
                getCurrentPosition={getCurrentPosition}
                ref={mergeRefs([fieldRef, ref])}
                {...parseRulesForInputAttributes(rules)}
                {...field}
                onTextValueChange={(text) => {
                    onChange(text.length ? {} : null);
                    props.onChange?.(text.length ? {} : null);
                    setTextValue(text);
                    onTextValueChange?.(text);
                }}
                textValue={textValue}
                state={state}
                suggestions={suggestions}
                isLoading={isGeocodingLoading || isReverseGeocodingLoading}
                isCurrentPositionUsed={isCurrentPositionUsed}
                setIsCurrentPositionUsed={setIsCurrentPositionUsed}
                {...props}
                onChange={(address) => {
                    onChange(address);
                    props.onChange?.(address);
                    setTextValue(formatAddress(address));
                }}
            />
            <FormFieldMessage state={state} error={error} messageWrapperProps={messageWrapperProps} />
        </>
    );
};
